import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import adminRequestApi from '@/api/admin-request.api';
import { contractsApi } from '@/api/contracts.api';
import { useSignupGeolocation } from '@/hooks/use-signup-geolocation';
import { COUNTRIES } from '@/lib/countries';
import { useSearchParams } from 'react-router-dom';
import type { User } from '@/types/auth.types';
import type { BecomeAdminCopy, BecomeAdminFormValues, BecomeAdminStepId } from './types';

type ToastPayload = {
  title: string;
  description: string;
  variant?: 'default' | 'destructive';
};

type UseBecomeAdminFormParams = {
  t: BecomeAdminCopy;
  user: User | null;
  toast: (payload: ToastPayload) => void;
};

const DEFAULT_PHONE_COUNTRY = 'Togo';

const resolvePhoneCountry = (country?: string | null) =>
  COUNTRIES.some((item) => item.name === country) ? country : DEFAULT_PHONE_COUNTRY;

const STEP_IDS: BecomeAdminStepId[] = ['company', 'representative', 'documents'];
const PAYMENT_MODAL_VALUE = 'payment';

const parseStepParam = (step: string | null) => {
  if (!step) return 0;
  const normalized = step.toLowerCase();
  const numericStep = Number(normalized);

  if (Number.isInteger(numericStep) && numericStep >= 1 && numericStep <= STEP_IDS.length) {
    return numericStep - 1;
  }

  const namedStep = STEP_IDS.indexOf(normalized as BecomeAdminStepId);
  return namedStep >= 0 ? namedStep : 0;
};

const isPaymentModalParam = (modal: string | null) => modal === PAYMENT_MODAL_VALUE;
type PersistedBecomeAdminValues = Omit<BecomeAdminFormValues, 'files' | 'signedContract' | 'cardNumber' | 'cardExpiry' | 'cardCVC'>;

const DRAFT_STORAGE_PREFIX = 'yodi:become-admin:draft';

const getDraftKey = (user: User | null) => `${DRAFT_STORAGE_PREFIX}:${user?.id || user?.username || 'anonymous'}`;

const toPersistedValues = (values: BecomeAdminFormValues): PersistedBecomeAdminValues => ({
  companyName: values.companyName,
  registrationNumber: values.registrationNumber,
  companyAddress: values.companyAddress,
  sector: values.sector,
  companyEmail: values.companyEmail,
  companyCountry: values.companyCountry,
  companyPhone: values.companyPhone,
  companyWebsite: values.companyWebsite,
  repName: values.repName,
  repRole: values.repRole,
  repEmail: values.repEmail,
  repCountry: values.repCountry,
  repPhone: values.repPhone,
  username: values.username,
  password: values.password,
  useSSO: values.useSSO,
  accepted: values.accepted,
  rgpdAccepted: values.rgpdAccepted,
});

const readDraftValues = (user: User | null, fallback: BecomeAdminFormValues): BecomeAdminFormValues => {
  if (typeof window === 'undefined') return fallback;

  try {
    const rawDraft = window.localStorage.getItem(getDraftKey(user));
    if (!rawDraft) return fallback;

    return {
      ...fallback,
      ...(JSON.parse(rawDraft) as Partial<PersistedBecomeAdminValues>),
      files: [],
      signedContract: null,
      cardNumber: '',
      cardExpiry: '',
      cardCVC: '',
    };
  } catch {
    return fallback;
  }
};

const writeDraftValues = (user: User | null, values: BecomeAdminFormValues) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(getDraftKey(user), JSON.stringify(toPersistedValues(values)));
  } catch {
    // Ignore unavailable storage/quota errors. The form must stay usable.
  }
};

const clearDraftValues = (user: User | null) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(getDraftKey(user));
  } catch {
    // Ignore unavailable storage errors.
  }
};
const createDefaultValues = (user: User | null): BecomeAdminFormValues => ({
  companyName: '',
  registrationNumber: '',
  companyAddress: '',
  sector: '',
  companyEmail: '',
  companyCountry: resolvePhoneCountry(user?.country),
  companyPhone: '',
  companyWebsite: '',
  repName: user?.name ?? '',
  repRole: '',
  repEmail: user?.email ?? '',
  repCountry: resolvePhoneCountry(user?.country),
  repPhone: '',
  username: typeof user?.username === 'string' ? user.username : '',
  password: '',
  useSSO: false,
  files: [],
  signedContract: null,
  accepted: false,
  rgpdAccepted: false,
  cardNumber: '',
  cardExpiry: '',
  cardCVC: '',
});

const createInitialValues = (user: User | null): BecomeAdminFormValues =>
  readDraftValues(user, createDefaultValues(user));

const trimValues = (values: BecomeAdminFormValues) => ({
  companyName: values.companyName.trim(),
  registrationNumber: values.registrationNumber.trim(),
  companyAddress: values.companyAddress.trim(),
  sector: values.sector.trim(),
  companyEmail: values.companyEmail.trim(),
  companyPhone: values.companyPhone.trim(),
  companyWebsite: values.companyWebsite.trim(),
  repName: values.repName.trim(),
  repRole: values.repRole.trim(),
  repEmail: values.repEmail.trim(),
  repPhone: values.repPhone.trim(),
  username: values.username.trim(),
  password: values.password.trim(),
});
const isStepComplete = (values: BecomeAdminFormValues, step: number) => {
  const clean = trimValues(values);

  if (step === 0) {
    return Boolean(
      clean.companyName &&
        clean.registrationNumber &&
        clean.companyAddress &&
        clean.sector &&
        clean.companyEmail &&
        clean.companyPhone,
    );
  }

  if (step === 1) {
    return Boolean(clean.repName && clean.repEmail && clean.username && (values.useSSO || clean.password));
  }

  return Boolean(values.files.length > 0 && values.signedContract && values.accepted && values.rgpdAccepted);
};

export function useBecomeAdminForm({ t, user, toast }: UseBecomeAdminFormParams) {
  const geo = useSignupGeolocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStep = parseStepParam(searchParams.get('step'));
  const initialPaymentModal = isPaymentModalParam(searchParams.get('modal'));
  const [values, setValues] = useState<BecomeAdminFormValues>(() => createInitialValues(user));
  const [activeStep, setActiveStep] = useState(initialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModalState] = useState(initialPaymentModal);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [contractTemplateHtml, setContractTemplateHtml] = useState<string | null>(null);
  const [geoApplied, setGeoApplied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const skipNextDraftWriteRef = useRef(false);
  const syncDeepLink = useCallback(
    (stepIndex: number, paymentOpen = showPaymentModal) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('step', STEP_IDS[stepIndex] || STEP_IDS[0]);

      if (paymentOpen) {
        nextParams.set('modal', PAYMENT_MODAL_VALUE);
      } else if (nextParams.get('modal') === PAYMENT_MODAL_VALUE) {
        nextParams.delete('modal');
      }

      if (nextParams.toString() !== searchParams.toString()) {
        setSearchParams(nextParams, { replace: true });
      }
    },
    [searchParams, setSearchParams, showPaymentModal],
  );

  useEffect(() => {
    const nextStep = parseStepParam(searchParams.get('step'));
    const nextPaymentModal = isPaymentModalParam(searchParams.get('modal'));

    setActiveStep((currentStep) => (currentStep === nextStep ? currentStep : nextStep));
    setShowPaymentModalState((currentOpen) => (currentOpen === nextPaymentModal ? currentOpen : nextPaymentModal));
  }, [searchParams]);

  const setStep = useCallback(
    (stepIndex: number, paymentOpen = showPaymentModal) => {
      const boundedStep = Math.min(Math.max(stepIndex, 0), STEP_IDS.length - 1);
      setActiveStep(boundedStep);
      syncDeepLink(boundedStep, paymentOpen);
    },
    [showPaymentModal, syncDeepLink],
  );

  const setShowPaymentModal = useCallback(
    (open: boolean) => {
      setShowPaymentModalState(open);
      syncDeepLink(activeStep, open);
    },
    [activeStep, syncDeepLink],
  );

  useEffect(() => {
    let cancelled = false;

    const loadAdminContractTemplate = async () => {
      try {
        const template = await contractsApi.getAdminContractTemplate();
        if (!cancelled) {
          setContractTemplateHtml(template.contentHtml || null);
        }
      } catch {
        if (!cancelled) {
          setContractTemplateHtml(null);
        }
      }
    };

    void loadAdminContractTemplate();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (skipNextDraftWriteRef.current) {
      skipNextDraftWriteRef.current = false;
      return;
    }

    writeDraftValues(user, values);
  }, [user?.id, user?.username, values]);
  useEffect(() => {
    if (!geo || geoApplied) return;

    const profileCountry = resolvePhoneCountry(user?.country);
    setValues((previousValues) => ({
      ...previousValues,
      companyCountry:
        !previousValues.companyPhone.trim() && profileCountry === DEFAULT_PHONE_COUNTRY
          ? geo.countryName
          : previousValues.companyCountry,
      companyPhone:
        !previousValues.companyPhone.trim() && profileCountry === DEFAULT_PHONE_COUNTRY
          ? `${geo.dial} `
          : previousValues.companyPhone,
      repCountry:
        !previousValues.repPhone.trim() && profileCountry === DEFAULT_PHONE_COUNTRY
          ? geo.countryName
          : previousValues.repCountry,
      repPhone:
        !previousValues.repPhone.trim() && profileCountry === DEFAULT_PHONE_COUNTRY
          ? `${geo.dial} `
          : previousValues.repPhone,
    }));
    setGeoApplied(true);
  }, [geo, geoApplied, user?.country]);
  const completion = useMemo(() => {
    const required = [
      values.companyName,
      values.registrationNumber,
      values.companyAddress,
      values.sector,
      values.companyEmail,
      values.companyPhone,
      values.repName,
      values.repEmail,
      values.username,
      values.useSSO ? 'sso' : values.password,
      values.files.length > 0 ? 'files' : '',
      values.signedContract ? 'contract' : '',
      values.accepted ? 'terms' : '',
      values.rgpdAccepted ? 'privacy' : '',
    ];

    const done = required.filter(Boolean).length;
    return Math.round((done / required.length) * 100);
  }, [values]);

  const canProceed = useMemo(() => isStepComplete(values, activeStep), [activeStep, values]);

  const setField = <Key extends keyof BecomeAdminFormValues>(key: Key, value: BecomeAdminFormValues[Key]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const onFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const incoming = Array.from(fileList).slice(0, 10);
    setValues((prev) => ({ ...prev, files: [...prev.files, ...incoming].slice(0, 10) }));
  };

  const removeFile = (index: number) => {
    setValues((prev) => ({ ...prev, files: prev.files.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const rejectStep = (description: string) => {
    toast({ title: t.validation, description, variant: 'destructive' });
    return false;
  };

  const validateStep = (step = activeStep) => {
    const clean = trimValues(values);

    if (step === 0) {
      if (!clean.companyName) return rejectStep(t.companyNameRequired);
      if (!clean.registrationNumber) return rejectStep(t.registrationNumberRequired);
      if (!clean.companyAddress) return rejectStep(t.companyAddressRequired);
      if (!clean.sector) return rejectStep(t.sectorActivityRequired);
      if (!clean.companyEmail) return rejectStep(t.companyEmailRequired);
      if (!clean.companyPhone) return rejectStep(t.companyPhoneRequired);
      return true;
    }

    if (step === 1) {
      if (!clean.repName) return rejectStep(t.repNameRequired);
      if (!clean.repEmail) return rejectStep(t.repEmailRequired);
      if (!clean.username) return rejectStep(t.usernameRequired);
      if (!values.useSSO && !clean.password) return rejectStep(t.passwordOrSSORequired);
      return true;
    }

    if (values.files.length === 0) return rejectStep(t.documentsRequired);
    if (!values.signedContract) return rejectStep(t.signedContractRequired);
    if (!values.accepted) return rejectStep(t.termsAcceptRequired);
    if (!values.rgpdAccepted) return rejectStep(t.rgpdAcceptRequired);
    return true;
  };

  const resetForm = () => {
    clearDraftValues(user);
    skipNextDraftWriteRef.current = true;
    setValues(createDefaultValues(user));
    setShowPaymentModal(false);
    setStep(0, false);
  };

  const submit = async () => {
    if (!validateStep(2)) return;

    setIsSubmitting(true);
    try {
      const clean = trimValues(values);
      const create = await adminRequestApi.createRequest({
        companyName: clean.companyName,
        companyRegistrationNumber: clean.registrationNumber,
        companyAddress: clean.companyAddress,
        sector: clean.sector,
        companyEmail: clean.companyEmail,
        companyPhone: clean.companyPhone,
        companyWebsite: clean.companyWebsite || undefined,
        representative: {
          name: clean.repName,
          role: clean.repRole,
          email: clean.repEmail,
          phone: clean.repPhone,
        },
        account: {
          username: clean.username,
          password: values.useSSO ? undefined : clean.password,
          useSSO: values.useSSO,
        },
      });

      const requestId = create?.id ?? create?.requestId ?? create;
      const docsToUpload = values.signedContract ? [...values.files, values.signedContract] : values.files;
      await adminRequestApi.uploadDocuments(requestId, docsToUpload);
      await adminRequestApi.acceptContract(requestId);
      toast({ title: t.submitted, description: t.adminRequestSent });
      resetForm();
    } catch (error) {
      console.error(error);
      toast({ title: t.error, description: t.submitFailed, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goNext = async () => {
    if (!validateStep(activeStep)) return;
    if (activeStep < 2) {
      setStep(activeStep + 1);
      return;
    }
    await submit();
  };

  const goPrev = () => setStep(activeStep - 1);

  const processCardPayment = async () => {
    const cardNumberDigits = values.cardNumber.replace(/\D/g, '');
    const cardExpiryDigits = values.cardExpiry.replace(/\D/g, '');
    const cardCvcDigits = values.cardCVC.replace(/\D/g, '');
    const expiryMonth = Number(cardExpiryDigits.slice(0, 2));
    const cardLooksValid =
      cardNumberDigits.length >= 12 &&
      cardNumberDigits.length <= 19 &&
      cardExpiryDigits.length === 4 &&
      expiryMonth >= 1 &&
      expiryMonth <= 12 &&
      (cardCvcDigits.length === 3 || cardCvcDigits.length === 4);

    if (!cardLooksValid) {
      toast({ title: t.payment, description: t.paymentCardRequired, variant: 'destructive' });
      return;
    }

    setProcessingPayment(true);
    try {
      await new Promise((resolve) => window.setTimeout(resolve, 900));
      toast({ title: t.payment, description: t.paymentSuccess });
      setShowPaymentModal(false);
    } catch (error) {
      console.error(error);
      toast({ title: t.payment, description: t.paymentFailed, variant: 'destructive' });
    } finally {
      setProcessingPayment(false);
    }
  };

  return {
    activeStep,
    canProceed,
    completion,
    contractTemplateHtml,
    fileInputRef,
    isSubmitting,
    processingPayment,
    showPaymentModal,
    values,
    goNext,
    goPrev,
    onFiles,
    processCardPayment,
    removeFile,
    setField,
    setShowPaymentModal,
  };
}


